import React, { useEffect } from 'react';

import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';

import { createChatSession, saveResetLeo } from '../../slices/LeoSlice';
import { AppDispatch, RootState } from '../../types';
import NeoLeoChat from './Chat/NeoLeoChat';
import { NeoLeoBodyWrapper } from './NeoLeoBodyWrapper';
import NeoLeoChatFooter from './NeoLeoChatFooter';
import NeoLeoHeader from './NeoLeoHeader';
import NeoLeoHistoryIndex from './NeoLeoHistoryIndex';
import './NeoLeoIndex.css';
import NeoLeoIntro from './NeoLeoIntro';
import SessionRetry from './SessionRetry';

interface NeoLeoPanelContainerProps {
  onClose: () => void;
  setShowHistory: React.Dispatch<React.SetStateAction<boolean>>;
  showHistory: boolean;
  isFullScreen?: boolean;
  isMobile?: boolean;
}

const NeoLeoPanelContainer: React.FC<NeoLeoPanelContainerProps> = ({
  onClose,
  setShowHistory,
  showHistory,
  isMobile,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    auth: { authUserDetail },
    leo: { showIntro, session, isSessionError },
  } = useSelector((state: RootState) => ({
    auth: state.auth,
    leo: state.leo,
  }));
  const agentId = authUserDetail?.id;

  const startNewSession = () => {
    dispatch(saveResetLeo());
    dispatch(createChatSession(agentId!));
  };

  useEffect(() => {
    setShowHistory(false);
  }, [session, setShowHistory]);

  useEffect(
    () => () => {
      dispatch(saveResetLeo());
    },
    [dispatch],
  );

  if (!session?.id && isSessionError) {
    return (
      <div
        className='flex-grow flex flex-col h-full z-10'
        aria-label='leo-chat-index'
      >
        <div className='flex-grow flex flex-col h-full'>
          <NeoLeoBodyWrapper>
            <NeoLeoHeader
              isShowingHistory={showHistory}
              setShowHistory={setShowHistory}
              startNewSession={startNewSession}
              onClose={onClose}
              isMobile={isMobile}
              hideHistory
            />
            <SessionRetry />
          </NeoLeoBodyWrapper>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className='flex-grow flex flex-col h-full z-10'
      layout
      transition={{ duration: 0.1 }}
      aria-label='leo-chat-index'
    >
      <div className='flex-grow flex flex-col h-full'>
        <NeoLeoHeader
          setShowHistory={setShowHistory}
          isShowingHistory={showHistory}
          startNewSession={startNewSession}
          onClose={onClose}
          isMobile={isMobile}
        />
        <NeoLeoBodyWrapper>
          {showHistory ? (
            <NeoLeoHistoryIndex hideHistory={() => setShowHistory(false)} />
          ) : showIntro ? (
            <NeoLeoIntro />
          ) : (
            <NeoLeoChat isShowingHistory={showHistory} />
          )}
        </NeoLeoBodyWrapper>
        <NeoLeoChatFooter isIntro={showIntro} />
      </div>
    </motion.div>
  );
};

export default NeoLeoPanelContainer;
